import React, { useState, useEffect, useContext, useCallback } from 'react';
import { fmtDate as _fmtDate, humanizeDates } from '@/lib/dates';
import { toast } from 'sonner';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import SidebarShell from '@/components/wom/SidebarShell';
import { moduleIcon, moduleColor } from '@/lib/moduleIdentity';
import ServiceRequestTable from '@/components/wom/ServiceRequestTable';
import ServiceRequestDetail from '@/components/wom/ServiceRequestDetail';
import ServiceRequestForm from '@/components/wom/ServiceRequestForm';
import WorkOrderDetail from '@/components/wom/WorkOrderDetail';
import OperationsDashboard from './OperationsDashboard';
import CustomerManager from './CustomerManager';
import LocationManager from './LocationManager';
import DispatchBoard from './DispatchBoard';
import ReportsView from './ReportsView';
import FollowUpsView from './FollowUpsView';
import HistoricalBillingView from './HistoricalBillingView';
import HistoricalPhotosPendingView from './HistoricalPhotosPendingView';
import AdministrationView from '@/components/admin/AdministrationView';
import { StatusBadge } from '@/components/wom/StatusBadge';
import { urgencyLabel } from '@/components/wom/theme';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { WorkOrderPurgeDialog, showPurgeUndoToast } from './WorkOrderPurgeDialog';
import { MobileRecordCard, MobileRecordList } from '@/components/common/mobile/MobileRecordCard';
import {
  LayoutDashboard, ClipboardList, Wrench, Truck, Building2, Plus,
  MapPin, Users, BarChart3, Settings, Construction, Search, X, DollarSign, BellRing, Archive, Camera, Trash2,
} from 'lucide-react';

const isGlobalAdmin = (user) => user?.role === 'owner' || (user?.capabilities || []).includes('admin.controls');

const COMPLETED_WO = ['Completed', 'Closed by Admin', 'Closed', 'Work Completed'];
const AWAITING_WO_STATUSES = [
  'Approved - Awaiting TFS Work Order Processing',
  'Client Authorized — Awaiting Work Order',
  'Proposal Required',
];
const srBucket = (r) => {
  const s = r.status;
  if (s === 'Work Order Created') return COMPLETED_WO.includes(r.linked_wo_status) ? 'completed' : 'wo_created';
  if (r.work_order_id && COMPLETED_WO.includes(r.linked_wo_status)) return 'completed';
  if (r.work_order_id) return 'wo_created';
  if (s === 'Submitted') return 'new';
  if (AWAITING_WO_STATUSES.includes(s)) return 'awaiting_wo';
  if (s === 'Information Requested') return 'info';
  if (s === 'On Hold') return 'hold';
  if (s === 'Declined') return 'declined';
  return 'other';
};
const SR_TABS = [
  { key: 'all', label: 'All' },
  { key: 'new', label: 'New' },
  { key: 'awaiting_wo', label: 'Awaiting WO' },
  { key: 'wo_created', label: 'WO Created' },
  { key: 'completed', label: 'Completed' },
  { key: 'info', label: 'Info Requested' },
  { key: 'hold', label: 'On Hold' },
  { key: 'declined', label: 'Declined' },
];
const TAB_BY_STATUS = {
  Submitted: 'new',
  'Approved - Awaiting TFS Work Order Processing': 'awaiting_wo',
  'Information Requested': 'info',
  'On Hold': 'hold',
  Declined: 'declined',
  'Work Order Created': 'wo_created',
};

const PageHead = ({ eyebrow, title, subtitle, actions }) => (
  <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
    <div className="min-w-0">
      <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#8FC93A]">{eyebrow}</div>
      <h1 className="text-2xl font-extrabold text-[#0E2A47] sm:text-3xl">{title}</h1>
      {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
    </div>
    {actions && <div className="w-full sm:w-auto sm:shrink-0 [&_button]:w-full sm:[&_button]:w-auto">{actions}</div>}
  </div>
);

const BillingApprovalsView = ({ onSelect }) => {
  const { token } = useContext(AuthContext);
  const [rows, setRows] = useState([]);
  useEffect(() => {
    axios.get(`${API}/billing/pending-approval`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setRows(r.data || [])).catch(() => {});
  }, [token]);
  return (
    <div data-testid="billing-approvals-view">
      <PageHead eyebrow="Global Administrator" title="Billing Approvals" subtitle="Work orders awaiting your final billing approval." />
      {rows.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-slate-400" data-testid="billing-approvals-empty">No work orders awaiting approval.</div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500"><tr>
              <th className="px-4 py-2">WO #</th><th className="px-4 py-2">Customer</th><th className="px-4 py-2">Location</th>
              <th className="px-4 py-2 text-right">Total</th><th className="px-4 py-2 text-right">Margin</th><th /></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={r.id} data-testid={`billing-approval-${r.id.slice(0, 8)}`}>
                  <td className="px-4 py-2 font-mono">{r.tfs_work_order_number}</td>
                  <td className="px-4 py-2">{r.customer_name}</td>
                  <td className="px-4 py-2">{r.service_location}</td>
                  <td className="px-4 py-2 text-right">${Number(r.proposed_billing_total || 0).toFixed(2)}</td>
                  <td className="px-4 py-2 text-right">{r.margin_pct ?? '—'}%</td>
                  <td className="px-4 py-2 text-right"><button onClick={() => onSelect(r.id)} data-testid={`billing-review-btn-${r.id.slice(0, 8)}`} className="text-xs font-semibold text-[#0E2A47] underline">Review</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const ServiceRequestsView = ({ onSelect, initialStatus, onNewRequest }) => {
  const { token, user } = useContext(AuthContext);
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
  const [all, setAll] = useState([]);
  const [active, setActive] = useState(TAB_BY_STATUS[initialStatus] || 'all');
  const [qCust, setQCust] = useState('');
  const [urgency, setUrgency] = useState('All');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortKey, setSortKey] = useState('submitted_at');
  const [sortDir, setSortDir] = useState('desc');
  const [selected, setSelected] = useState(new Set());
  const [purging, setPurging] = useState(false);
  const canPurge = user?.role === 'owner';
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const load = useCallback(async () => {
    const r = await axios.get(`${API}/service-requests`, authHeaders);
    setAll(r.data || []);
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const counts = SR_TABS.reduce((acc, t) => {
    acc[t.key] = t.key === 'all' ? all.length : all.filter((r) => srBucket(r) === t.key).length;
    return acc;
  }, {});

  const onSort = (col) => {
    if (sortKey === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(col); setSortDir(col === 'submitted_at' ? 'desc' : 'asc'); }
  };

  let rows = all.filter((r) => {
    if (active !== 'all' && srBucket(r) !== active) return false;
    if (urgency !== 'All' && urgencyLabel(r.request_type) !== urgency) return false;
    if (qCust.trim()) {
      const hay = `${r.customer_name || ''} ${r.location_name || ''}`.toLowerCase();
      if (!hay.includes(qCust.trim().toLowerCase())) return false;
    }
    if (dateFrom && new Date(r.submitted_at) < new Date(dateFrom)) return false;
    if (dateTo && new Date(r.submitted_at) > new Date(`${dateTo}T23:59:59`)) return false;
    return true;
  });
  rows = [...rows].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    let va; let vb;
    if (sortKey === 'submitted_at') { va = new Date(a.submitted_at || 0); vb = new Date(b.submitted_at || 0); }
    else if (sortKey === 'customer_name') { va = `${a.customer_name || ''} ${a.location_name || ''}`.toLowerCase(); vb = `${b.customer_name || ''} ${b.location_name || ''}`.toLowerCase(); }
    else { va = (a[sortKey] || '').toString().toLowerCase(); vb = (b[sortKey] || '').toString().toLowerCase(); }
    return va < vb ? -dir : va > vb ? dir : 0;
  });

  const urgencyOpts = ['All', ...Array.from(new Set(all.map((r) => urgencyLabel(r.request_type))))];
  const ctl = 'rounded-md border border-gray-200 px-3 py-2 text-sm text-slate-600';
  const lbl = 'text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1';
  const hasFilters = qCust || urgency !== 'All' || dateFrom || dateTo;

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <PageHead eyebrow="Operations" title="Service Requests" subtitle="Review, approve and route client maintenance requests." />
        <button onClick={onNewRequest} data-testid="ops-new-request-btn"
          className="shrink-0 rounded-md bg-[#0E2A47] px-4 py-2 text-sm font-semibold text-white hover:bg-[#16324F]">
          + New Request (on behalf)
        </button>
      </div>
      <div className="flex flex-wrap gap-2 mb-4" data-testid="sr-filter-tabs">
        {SR_TABS.map((f) => (
          <button key={f.key} onClick={() => setActive(f.key)} data-testid={`sr-filter-${f.key}`}
            className={`rounded-full px-3.5 py-1.5 text-sm font-medium border transition-colors ${
              active === f.key ? 'bg-[#0E2A47] text-white border-[#0E2A47]' : 'bg-white text-slate-600 border-gray-200 hover:bg-gray-50'
            }`}>
            {f.label} <span className={active === f.key ? 'text-white/70' : 'text-slate-400'} data-testid={`sr-count-${f.key}`}>({counts[f.key] || 0})</span>
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-end gap-3 mb-4" data-testid="sr-filter-controls">
        <div className="flex flex-col">
          <label className={lbl}>Customer / Location</label>
          <input value={qCust} onChange={(e) => setQCust(e.target.value)} placeholder="Search…" data-testid="sr-filter-customer" className={`${ctl} w-48`} />
        </div>
        <div className="flex flex-col">
          <label className={lbl}>Urgency</label>
          <select value={urgency} onChange={(e) => setUrgency(e.target.value)} data-testid="sr-filter-urgency" className={ctl}>
            {urgencyOpts.map((u) => <option key={u} value={u}>{u === 'All' ? 'All urgencies' : u}</option>)}
          </select>
        </div>
        <div className="flex flex-col">
          <label className={lbl}>Submitted from</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="sr-filter-date-from" className={ctl} />
        </div>
        <div className="flex flex-col">
          <label className={lbl}>Submitted to</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="sr-filter-date-to" className={ctl} />
        </div>
        {hasFilters && (
          <button onClick={() => { setQCust(''); setUrgency('All'); setDateFrom(''); setDateTo(''); }} data-testid="sr-filter-clear" className="pb-2 text-xs text-slate-500 underline">Clear filters</button>
        )}
        <div className="ml-auto pb-2 text-xs text-slate-400" data-testid="sr-result-count">{rows.length} shown</div>
      </div>
      {canPurge && selected.size > 0 && (
        <div className="flex items-center justify-between rounded-lg border border-red-200 bg-red-50 px-4 py-2" data-testid="sr-bulk-purge-bar">
          <div className="text-sm font-medium text-red-800">{selected.size} selected for purge</div>
          <div className="flex items-center gap-2">
            <button className="text-xs text-slate-500 underline" onClick={() => setSelected(new Set())}>Clear</button>
            <button disabled={purging} data-testid="sr-bulk-purge-btn"
              className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50"
              onClick={async () => {
                const chosen = rows.filter((r) => selected.has(r.id));
                const nums = chosen.map((r) => r.request_number);
                const withWo = chosen.filter((r) => r.work_order_id).map((r) => r.request_number);
                const msg = `Permanently purge ${nums.length} service request(s):\n${nums.join(', ')}` +
                  (withWo.length ? `\n\nThese also have linked work orders that will be purged: ${withWo.join(', ')}` : '') +
                  `\n\nThis cannot be undone. Master customers/locations are preserved.`;
                if (!window.confirm(msg)) return;
                setPurging(true);
                try {
                  await axios.post(`${API}/service-requests/purge`, { ids: [...selected] }, authHeaders);
                  toast.success(`Purged ${nums.length} service request(s)`);
                  setSelected(new Set()); await load();
                } catch (e) { toast.error(e.response?.data?.detail || 'Bulk purge failed'); }
                finally { setPurging(false); }
              }}>{purging ? 'Purging…' : `Purge ${selected.size}`}</button>
          </div>
        </div>
      )}
      <ServiceRequestTable requests={rows} onSelect={onSelect} sortKey={sortKey} sortDir={sortDir} onSort={onSort}
        selectable={canPurge} selected={selected} onToggle={toggle}
        onToggleAll={(checked) => setSelected(checked ? new Set(rows.map((r) => r.id)) : new Set())} />
    </div>
  );
};

const WO_STATUS_FILTERS = ['All', 'Overdue', 'Awaiting Work Order Processing', 'Open - Unassigned', 'Assigned', 'Scheduled', 'Dispatched', 'En Route', 'In Progress', 'Awaiting Authorization', 'Awaiting Administrative Closeout', 'Completed', 'On Hold', 'Cancelled', 'Open', 'Closed by Admin'];
const WO_PRIORITY_FILTERS = ['All', 'P1', 'P2', 'P3', 'P4', 'P5'];
const WO_CLOSED = ['Cancelled', 'Closed by Admin', 'Closed', 'Completed', 'Work Completed'];

const woRequiredDate = (w) => w.required_completion_date || w.required_service_date;
const isWoActive = (w) => !WO_CLOSED.includes(w.status);
const isWoOverdue = (w) => {
  const d = woRequiredDate(w);
  if (!d || !isWoActive(w)) return false;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return new Date(d) < today;
};
const isWoUnscheduled = (w) => isWoActive(w) && !(w.scheduled_date && String(w.scheduled_date).trim());
const matchesSpecial = (w, special) => {
  if (special === 'p1_emergency') return w.priority_level === 'P1' && (isWoUnscheduled(w) || isWoOverdue(w));
  if (special === 'overdue_other') return isWoOverdue(w) && w.priority_level !== 'P1';
  return true;
};

const WorkOrdersView = ({ onSelect, initialFilter, onCreateNew }) => {
  const { token, user } = useContext(AuthContext);
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
  const canPurge = isGlobalAdmin(user);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [purgeOpen, setPurgeOpen] = useState(false);
  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const [search, setSearch] = useState('');
  const [statusF, setStatusF] = useState(initialFilter?.overdue ? 'Overdue' : (initialFilter?.status || 'All'));
  const [prioF, setPrioF] = useState(initialFilter?.priority || 'All');
  const [special, setSpecial] = useState(initialFilter?.special || null);
  const [customerF, setCustomerF] = useState(initialFilter?.customer_id || null);
  const [customerLabel, setCustomerLabel] = useState(initialFilter?.customer_name || null);
  const [tradeF, setTradeF] = useState(initialFilter?.trade_type || null);
  const [startF, setStartF] = useState(initialFilter?.start || null);
  const [endF, setEndF] = useState(initialFilter?.end || null);
  useEffect(() => {
    setStatusF(initialFilter?.overdue ? 'Overdue' : (initialFilter?.status || 'All'));
    setPrioF(initialFilter?.priority || 'All');
    setSpecial(initialFilter?.special || null);
    setCustomerF(initialFilter?.customer_id || null);
    setCustomerLabel(initialFilter?.customer_name || null);
    setTradeF(initialFilter?.trade_type || null);
    setStartF(initialFilter?.start || null);
    setEndF(initialFilter?.end || null);
  }, [initialFilter]);
  const loadWos = useCallback(() => {
    axios.get(`${API}/work-orders`, authHeaders).then((r) => setItems(r.data || [])).catch(() => {});
  }, [token]);
  useEffect(() => { loadWos(); }, [loadWos]);

  const fmtShort = (s) => (_fmtDate(s) || s);
  const customerName = customerF ? (customerLabel || items.find((w) => w.customer_id === customerF)?.customer_name || 'Selected customer') : null;
  const SPECIAL_LABEL = { p1_emergency: 'P1 Emergency · Unscheduled / Overdue', overdue_other: 'Other Overdue (P2–P5)' };
  const rangeActive = !!(startF || endF);
  // Overdue-type views are current-state and ignore the date range (matches the report backend).
  const skipRange = special === 'p1_emergency' || special === 'overdue_other' || statusF === 'Overdue';
  const chips = [
    special && { key: 'special', label: SPECIAL_LABEL[special] || special, clear: () => setSpecial(null) },
    rangeActive && !skipRange && { key: 'range', label: `${startF ? fmtShort(startF) : '…'} → ${endF ? fmtShort(endF) : '…'}`, clear: () => { setStartF(null); setEndF(null); } },
    customerF && { key: 'customer', label: `Customer: ${customerName}`, clear: () => { setCustomerF(null); setCustomerLabel(null); } },
    tradeF && { key: 'trade', label: `Trade: ${tradeF}`, clear: () => setTradeF(null) },
    statusF !== 'All' && { key: 'status', label: statusF === 'Overdue' ? 'Overdue' : `Status: ${statusF}`, clear: () => setStatusF('All') },
    prioF !== 'All' && { key: 'priority', label: `Priority: ${prioF}`, clear: () => setPrioF('All') },
  ].filter(Boolean);

  const filtered = items.filter((w) => {
    if (special && !matchesSpecial(w, special)) return false;
    if (customerF && w.customer_id !== customerF) return false;
    if (tradeF && w.trade_type !== tradeF) return false;
    if (!skipRange && (startF || endF)) {
      const dr = (w.date_received || '').slice(0, 10);
      if (startF && (!dr || dr < startF)) return false;
      if (endF && (!dr || dr > endF)) return false;
    }
    if (statusF === 'Overdue') { if (!isWoOverdue(w)) return false; }
    else if (statusF !== 'All' && w.status !== statusF) return false;
    if (prioF !== 'All' && w.priority_level !== prioF) return false;
    if (search) {
      const q = search.toLowerCase();
      const hay = `${w.tfs_work_order_number || ''} ${w.legacy_work_order_number || ''} ${w.job_name || ''} ${w.customer_name || ''} ${w.service_location || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  // Keep the purge selection scoped to what's currently visible, so Select All / the
  // selection count and "Purge Selected" never include rows hidden by a filter change.
  useEffect(() => {
    setSelected((s) => {
      if (!s.size) return s;
      const visible = new Set(filtered.map((w) => w.id));
      const next = new Set([...s].filter((id) => visible.has(id)));
      return next.size === s.size ? s : next;
    });
  }, [search, statusF, prioF, special, customerF, tradeF, startF, endF, items]);

  return (
    <div>
      <PageHead eyebrow="Operations" title="All Work Orders"
        subtitle="Every work order is an interactive operational record. Open one to manage it."
        actions={onCreateNew ? (
          <Button onClick={onCreateNew} className="bg-[#0E2A47] hover:bg-[#16324F]" data-testid="wo-create-new-btn">
            <Plus size={16} className="mr-1.5" /> Create New Work Order
          </Button>
        ) : null} />
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input className="pl-9" placeholder="Search WO #, job, customer, location…" value={search}
            onChange={(e) => setSearch(e.target.value)} data-testid="wo-search-input" />
        </div>
        <select className="flex-1 min-w-[150px] rounded-md border border-gray-200 px-3 py-2 text-sm text-slate-600 sm:flex-none" value={statusF}
          onChange={(e) => setStatusF(e.target.value)} data-testid="wo-status-filter">
          {WO_STATUS_FILTERS.map((s) => <option key={s} value={s}>{s === 'All' ? 'All statuses' : s}</option>)}
        </select>
        <select className="flex-1 min-w-[150px] rounded-md border border-gray-200 px-3 py-2 text-sm text-slate-600 sm:flex-none" value={prioF}
          onChange={(e) => setPrioF(e.target.value)} data-testid="wo-priority-filter">
          {WO_PRIORITY_FILTERS.map((p) => <option key={p} value={p}>{p === 'All' ? 'All priorities' : p}</option>)}
        </select>
      </div>
      {chips.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="wo-filter-chips">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Filtered by:</span>
          {chips.map((c) => (
            <span key={c.key} data-testid={`wo-chip-${c.key}`}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#EAF0F8] border border-[#cfe0f2] px-3 py-1 text-xs font-medium text-[#0E2A47]">
              {c.label}
              <button onClick={c.clear} data-testid={`wo-chip-clear-${c.key}`} className="rounded-full hover:bg-[#0E2A47]/10 p-0.5" aria-label="Clear filter">
                <X size={12} />
              </button>
            </span>
          ))}
          <button onClick={() => { setSpecial(null); setCustomerF(null); setCustomerLabel(null); setTradeF(null); setStatusF('All'); setPrioF('All'); setStartF(null); setEndF(null); }}
            data-testid="wo-chip-clear-all" className="text-xs font-medium text-slate-500 hover:text-[#0E2A47] underline">Clear all</button>
        </div>
      )}
      {canPurge && selected.size > 0 && (
        <div className="flex items-center justify-between rounded-lg border border-red-200 bg-red-50 px-4 py-2 mb-3" data-testid="wo-bulk-purge-bar">
          <div className="text-sm font-medium text-red-800" data-testid="wo-purge-selection-count">{selected.size} work order{selected.size === 1 ? '' : 's'} selected</div>
          <div className="flex items-center gap-2">
            <button className="text-xs text-slate-500 underline" onClick={() => setSelected(new Set())} data-testid="wo-purge-clear-selection">Clear</button>
            <button data-testid="wo-bulk-purge-btn"
              className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700"
              onClick={() => setPurgeOpen(true)}>
              <Trash2 size={13} /> Purge Selected ({selected.size})
            </button>
          </div>
        </div>
      )}
      {/* Desktop: table (lg and up). Mobile/tablet: compact record cards. */}
      <Card className="hidden lg:block">
        <CardContent className="p-0">
          <Table data-testid="work-orders-table" containerClassName="max-h-[70vh] overflow-auto rounded-md">
            <TableHeader>
              <TableRow className="bg-gray-50">
                {canPurge && (
                  <TableHead className="sticky top-0 z-10 bg-gray-50 w-10">
                    <Checkbox
                      data-testid="wo-select-all"
                      checked={filtered.length > 0 && filtered.every((w) => selected.has(w.id))}
                      onCheckedChange={(v) => setSelected(v ? new Set(filtered.map((w) => w.id)) : new Set())}
                      aria-label="Select all work orders" />
                  </TableHead>
                )}
                <TableHead className="sticky top-0 z-10 bg-gray-50">WO #</TableHead><TableHead className="sticky top-0 z-10 bg-gray-50">Job</TableHead>
                <TableHead className="sticky top-0 z-10 bg-gray-50 hidden md:table-cell">Customer</TableHead>
                <TableHead className="sticky top-0 z-10 bg-gray-50">Priority</TableHead><TableHead className="sticky top-0 z-10 bg-gray-50">Status</TableHead>
                <TableHead className="sticky top-0 z-10 bg-gray-50 hidden lg:table-cell">Assigned</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((w) => (
                <TableRow key={w.id} className="cursor-pointer hover:bg-gray-50"
                  data-testid={`wo-row-${w.tfs_work_order_number}`} onClick={() => onSelect(w.id)}>
                  {canPurge && (
                    <TableCell className="w-10" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        data-testid={`wo-select-${w.tfs_work_order_number}`}
                        checked={selected.has(w.id)}
                        onCheckedChange={() => toggleSel(w.id)}
                        aria-label={`Select ${w.tfs_work_order_number}`} />
                    </TableCell>
                  )}
                  <TableCell className="font-mono text-xs font-semibold text-[#0E2A47]">{w.tfs_work_order_number}</TableCell>
                  <TableCell className="font-medium text-slate-800 max-w-[220px] truncate">{humanizeDates(w.job_name) || '—'}</TableCell>
                  <TableCell className="hidden md:table-cell text-sm text-slate-500">{w.customer_name || '—'}</TableCell>
                  <TableCell><span className="text-xs font-semibold text-slate-600">{w.priority_level || '—'}</span></TableCell>
                  <TableCell><span className="text-xs">{w.status || '—'}</span></TableCell>
                  <TableCell className="hidden lg:table-cell text-xs text-slate-500">{w.assigned_to_name || 'Unassigned'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {filtered.length === 0 && <div className="py-16 text-center text-slate-400 text-sm">No work orders match.</div>}
        </CardContent>
      </Card>

      {/* Mobile / tablet: compact cards — every field reorganised, no clipped columns, actions tappable */}
      <div className="lg:hidden">
        <MobileRecordList testid="wo-mobile-list">
          {filtered.map((w) => (
            <MobileRecordCard
              key={w.id}
              testid={`wo-card-${w.tfs_work_order_number}`}
              onClick={() => onSelect(w.id)}
              image={canPurge ? (
                <div onClick={(e) => e.stopPropagation()} className="pt-0.5">
                  <Checkbox
                    data-testid={`wo-card-select-${w.tfs_work_order_number}`}
                    checked={selected.has(w.id)}
                    onCheckedChange={() => toggleSel(w.id)}
                    aria-label={`Select ${w.tfs_work_order_number}`} />
                </div>
              ) : null}
              title={w.tfs_work_order_number}
              badge={<span className="whitespace-nowrap rounded-full bg-[#EAF0F8] px-2 py-0.5 text-[10px] font-semibold text-[#0E2A47]">{w.status || '—'}</span>}
              meta={[
                { label: 'Job', value: humanizeDates(w.job_name) || '—', full: true },
                { label: 'Customer', value: w.customer_name || '—' },
                { label: 'Priority', value: w.priority_level || '—' },
                { label: 'Assigned', value: w.assigned_to_name || 'Unassigned', full: true },
              ]}
            />
          ))}
        </MobileRecordList>
        {filtered.length === 0 && <div className="py-16 text-center text-slate-400 text-sm">No work orders match.</div>}
      </div>
      {canPurge && (
        <WorkOrderPurgeDialog open={purgeOpen} woIds={[...selected]} onOpenChange={setPurgeOpen}
          onPurged={(res) => { setSelected(new Set()); loadWos(); showPurgeUndoToast(res, token, loadWos); }} />
      )}
    </div>
  );
};

const Placeholder = ({ title }) => (
  <div>
    <PageHead eyebrow="Operations" title={title} />
    <Card>
      <CardContent className="py-20 text-center">
        <Construction size={40} className="mx-auto text-slate-300 mb-3" />
        <p className="text-slate-500 font-medium">{title} arrives in a later phase.</p>
        <p className="text-sm text-slate-400 mt-1">Phase 1 focuses on the Service Request → Approval → Operations queue.</p>
      </CardContent>
    </Card>
  </div>
);

// Standardized white sidebar branding panel (matches the other TFS app hubs):
// approved ROUT by TFS wordmark centered on solid white, above the navy navigation.
const RoutBrand = () => (
  <div className="flex items-center justify-center border-b border-white/10 bg-white px-4 py-4" data-testid="rout-brand">
    <img src={moduleIcon('rout_admin')} alt="ROUT" className="w-[70%] max-w-[136px] h-auto object-contain" data-testid="rout-brand-icon" />
  </div>
);


const OperationsPortal = () => {
  const { user, token } = useContext(AuthContext);
  const canCreateWo = ['owner', 'admin', 'manager', 'tfs_operations'].includes(user?.role);
  const [view, setView] = useState('dashboard');
  const [pendingBilling, setPendingBilling] = useState(0);
  const [followUpsDue, setFollowUpsDue] = useState(0);
  const [srPrefill, setSrPrefill] = useState(null);
  const [srReturn, setSrReturn] = useState('requests');
  useEffect(() => {
    if (user?.role !== 'owner') return;
    axios.get(`${API}/billing/pending-approval`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setPendingBilling((r.data || []).length)).catch(() => {});
  }, [user, token, view]);
  useEffect(() => {
    axios.get(`${API}/follow-up-reminders/summary`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setFollowUpsDue(r.data?.due || 0)).catch(() => {});
  }, [token, view]);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedWoId, setSelectedWoId] = useState(null);
  const [requestsInitialStatus, setRequestsInitialStatus] = useState(null);
  const [woFilter, setWoFilter] = useState({});
  const [promptAssignWo, setPromptAssignWo] = useState(false);
  const [woReturn, setWoReturn] = useState('work-orders');

  const openRequests = (status = null) => { setRequestsInitialStatus(status); setView('requests'); };
  const openDetail = (id) => { setSelectedId(id); setView('request-detail'); };
  const openWoDetail = (id, promptAssign = false, returnView = 'work-orders') => { setSelectedWoId(id); setPromptAssignWo(promptAssign); setWoReturn(returnView); setView('wo-detail'); };
  const openWorkOrders = (filter = {}) => { setWoFilter(filter); setView('work-orders'); };
  // Cross-hub deep link: /rout-admin?wo=<id> opens that work order directly.
  useEffect(() => {
    const woId = new URLSearchParams(window.location.search).get('wo');
    if (woId) openWoDetail(woId);
  }, []);
  const openFollowUpSR = (reminder) => {
    setSrPrefill({
      lockedCustomer: { id: reminder.customer_id, name: reminder.customer_name },
      prefill: {
        location_id: reminder.location_id, location_name: reminder.service_location,
        title: `Follow-up: ${reminder.tfs_work_order_number}`,
        description: `${reminder.recommendation}\n\nLinked follow-up from work order ${reminder.tfs_work_order_number}.`,
      },
    });
    setSrReturn('follow-ups');
    setView('new-request');
  };

  const navItems = [
    { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { key: 'requests', label: 'Service Requests', icon: ClipboardList },
    { key: 'work-orders', label: 'Work Orders', icon: Wrench },
    ...(user?.role === 'owner' ? [{ key: 'billing-approvals', label: 'Billing Approvals', icon: DollarSign, badge: pendingBilling || undefined }] : []),
    { key: 'historical-photos', label: 'Historical Photos', icon: Camera },
    ...(user?.role === 'owner' ? [{ key: 'historical-billing', label: 'Historical Billing', icon: Archive }] : []),
    { key: 'dispatch', label: 'Dispatch & Schedule', icon: Truck },
    { key: 'follow-ups', label: 'Follow-Ups', icon: BellRing, badge: followUpsDue || undefined },
    { key: 'reports', label: 'Reports', icon: BarChart3 },
    { key: 'administration', label: 'Administration', icon: Settings },
  ];

  const activeKey = ['request-detail', 'new-request'].includes(view) ? 'requests'
    : view === 'new-work-order' ? 'work-orders'
    : view === 'wo-detail' ? woReturn : view;

  const backAction = view === 'wo-detail'
    ? () => { setPromptAssignWo(false); setView(woReturn); }
    : null;

  return (
    <SidebarShell
      navItems={navItems} activeKey={activeKey} backAction={backAction}
      onNavigate={(k) => { setSelectedId(null); if (k === 'requests') setRequestsInitialStatus(null); if (k === 'work-orders') setWoFilter({}); setView(k); }}
      brandNode={<RoutBrand />} workspaceLabel="ROUT Administration"
      mobileNavGrid mobileNavAccent={moduleColor('rout_admin')} navAccent={moduleColor('rout_admin')}
      mobileBrand={<img src={moduleIcon('rout_admin')} alt="ROUT" className="h-9 w-9 object-contain" style={{ aspectRatio: '1 / 1' }} data-testid="rout-admin-mobile-brand" />}
    >
      {view === 'dashboard' && <OperationsDashboard onSelectRequest={openDetail} onOpenRequests={openRequests} onSelectWorkOrder={openWoDetail} onOpenWorkOrders={openWorkOrders} pendingBilling={pendingBilling} isGA={user?.role === 'owner'} onOpenBillingApprovals={() => setView('billing-approvals')} followUpsDue={followUpsDue} onOpenFollowUps={() => setView('follow-ups')} />}
      {view === 'requests' && <ServiceRequestsView onSelect={openDetail} initialStatus={requestsInitialStatus} onNewRequest={() => { setSrPrefill(null); setSrReturn('requests'); setView('new-request'); }} />}
      {view === 'new-request' && (
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#8FC93A]">Operations</div>
          <h1 className="text-3xl font-extrabold text-[#0E2A47] mb-6">New Service Request (Internal)</h1>
          <ServiceRequestForm mode="proxy"
            lockedCustomer={srPrefill?.lockedCustomer || null}
            prefill={srPrefill?.prefill || null}
            onSubmitted={() => { setSrPrefill(null); setView(srReturn); }}
            onCancel={() => { setSrPrefill(null); setView(srReturn); }} />
        </div>
      )}
      {view === 'request-detail' && (
        <ServiceRequestDetail requestId={selectedId} canReview canConvert
          onBack={() => setView('requests')} onConverted={(id) => openWoDetail(id, true)} />
      )}
      {view === 'wo-detail' && (
        <WorkOrderDetail workOrderId={selectedWoId} canManage promptAssignOnLoad={promptAssignWo}
          onOpenRequest={(srId) => { setSelectedId(srId); setView('request-detail'); }}
          onBack={() => { setPromptAssignWo(false); setView(woReturn); }} />
      )}
      {view === 'new-work-order' && (
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#8FC93A]">Operations</div>
          <h1 className="text-3xl font-extrabold text-[#0E2A47] mb-1">Create New Work Order</h1>
          <p className="text-sm text-slate-500 mb-6">Direct work order — no Service Request required. Use for approved jobs, internally-initiated work, or historical entry.</p>
          <ServiceRequestForm mode="proxy" createWorkOrder
            onWorkOrderCreated={(wo) => openWoDetail(wo.id, false, 'work-orders')}
            onCancel={() => setView('work-orders')} />
        </div>
      )}
      {view === 'work-orders' && <WorkOrdersView onSelect={openWoDetail} initialFilter={woFilter} onCreateNew={canCreateWo ? () => setView('new-work-order') : null} />}
      {view === 'billing-approvals' && <BillingApprovalsView onSelect={(id) => openWoDetail(id, false, 'billing-approvals')} />}
      {view === 'historical-billing' && <HistoricalBillingView />}
      {view === 'historical-photos' && <HistoricalPhotosPendingView onOpenWorkOrder={(id) => openWoDetail(id, false, 'historical-photos')} />}
      {view === 'customers' && <CustomerManager />}
      {view === 'locations' && <LocationManager />}
      {view === 'dispatch' && <DispatchBoard onSelect={(id) => openWoDetail(id, false, 'dispatch')} />}
      {view === 'follow-ups' && <FollowUpsView onOpenWorkOrder={(id) => openWoDetail(id, false, 'follow-ups')} onCreateServiceRequest={openFollowUpSR} />}
      {view === 'reports' && <ReportsView onDrill={(filter) => openWorkOrders(filter)} />}
      {view === 'administration' && <AdministrationView />}
    </SidebarShell>
  );
};

export default OperationsPortal;
